/**
 * The vend visit as the API describes it (contracts/openapi.yaml, VendSession)
 * and what each state means to the customer.
 *
 * The page never decides a state itself: it renders the server's snapshot and
 * offers exactly the server's `allowed_actions` (ADR-0024, ADR-0025).
 */

export const CHANNELS = ["paypay", "rakuten", "aupay", "docomopay", "wechat", "alipay", "aeonpay"] as const;
export type Channel = (typeof CHANNELS)[number];

export function isChannel(value: string | null | undefined): value is Channel {
  return !!value && (CHANNELS as readonly string[]).includes(value);
}

export type VendState =
  | "opened"
  | "selected"
  | "payment_pending"
  | "paid"
  | "driving"
  | "completed"
  | "failed"
  | "unknown"
  | "refund_due"
  | "refunded"
  | "canceled"
  | "expired";

export type VendAction = "pay" | "cancel" | "open_payment" | "request_refund";

export interface VendSession {
  vend_session_id: string;
  state: VendState;
  machine_id: string;
  channel: Channel;
  grid_no?: number | null;
  amount_yen?: number | null;
  payment?: { redirect_url?: string | null; deeplink?: string | null } | null;
  allowed_actions: VendAction[];
  unfulfilled: boolean;
  observed_at: string;
}

/** States after which nothing more will happen to the visit. */
export const TERMINAL: ReadonlySet<VendState> = new Set([
  "completed",
  "failed",
  "unknown",
  "refund_due",
  "refunded",
  "canceled",
  "expired",
]);

/**
 * States in which the customer still has something to do or wait for, so the
 * home screen offers to resume. `failed` and `refund_due` stay resumable while
 * a refund can still be requested.
 */
export function isResumable(v: VendSession): boolean {
  return !TERMINAL.has(v.state) || v.allowed_actions.includes("request_refund");
}

/** Every event kind the server writes; EventSource only delivers named events. */
export const EVENT_KINDS = [
  "opened",
  "machine_unavailable",
  "product_selected",
  "selection_changed",
  "payment_created",
  "paid",
  "drive_requested",
  "drive_sent",
  "dispensed",
  "dispense_failed",
  "dispense_unknown",
  "dispense_report_missing",
  "drive_unanswered",
  "drive_timed_out",
  "payment_unresolved",
  "refunded",
  "canceled",
  "expired",
  "resync",
] as const;

export type Tone = "waiting" | "action" | "progress" | "success" | "problem" | "neutral";

/**
 * How a state is shown: its tone and the step in progress on
 * Scan(0) > Product(1) > Pay(2) > Collect(3); 4 means every step is done.
 * A problem is always at Collect: the payment went through, the vend did not.
 */
export function presentation(state: VendState): { tone: Tone; step: 0 | 1 | 2 | 3 | 4 } {
  switch (state) {
    case "opened":
      return { tone: "waiting", step: 1 };
    case "selected":
      return { tone: "action", step: 2 };
    case "payment_pending":
      return { tone: "waiting", step: 2 };
    case "paid":
    case "driving":
      return { tone: "progress", step: 3 };
    case "completed":
      return { tone: "success", step: 4 };
    case "failed":
    case "refund_due":
    case "unknown":
      return { tone: "problem", step: 3 };
    case "refunded":
    case "canceled":
    case "expired":
      return { tone: "neutral", step: 0 };
  }
}

export function formatYen(amount: number, locale: string): string {
  return new Intl.NumberFormat(locale, { style: "currency", currency: "JPY" }).format(amount);
}
